import type { WineResult, QualityRating, RegionInfoMap } from '../types/wine.types'
import regionInfoData from '../data/region-info.json'

interface WineResultDisplayProps {
  result: WineResult
}

const regionInfo = regionInfoData as RegionInfoMap

function getAlertClass(quality: QualityRating): string {
  switch (quality) {
    case '+':
      return 'alert-success'
    case '~':
      return 'alert-warning'
    case '-':
      return 'alert-danger'
    default:
      return 'alert-warning'
  }
}

function getQualityLabel(quality: QualityRating): string {
  switch (quality) {
    case '+':
      return 'Good Pick!'
    case '~':
      return 'Mixed Results'
    case '-':
      return 'Challenging Year'
    default:
      return 'Unknown'
  }
}

function getQualityIcon(quality: QualityRating): string {
  switch (quality) {
    case '+':
      return '✓'
    case '~':
      return '~'
    case '-':
      return '✗'
    default:
      return '?'
  }
}

function getAgingRecommendation(vintage: string, quality: QualityRating): { label: string; description: string } {
  const currentYear = new Date().getFullYear()
  const vintageYear = parseInt(vintage)
  const age = currentYear - vintageYear

  if (quality === '-') {
    return {
      label: 'Drink Now',
      description: 'Wines from challenging years are best enjoyed young'
    }
  }

  if (quality === '~') {
    if (age >= 5) {
      return {
        label: 'Drink Now',
        description: 'Mixed vintage wines are reaching their peak'
      }
    }
    return {
      label: 'Drink Soon',
      description: 'Best enjoyed within the next 2-3 years'
    }
  }

  // Good quality wines
  if (age <= 3) {
    return {
      label: 'Hold or Drink',
      description: 'Young wine with aging potential; can enjoy now or cellar'
    }
  } else if (age <= 7) {
    return {
      label: 'Drinking Window',
      description: 'Entering prime drinking window; ideal for the next few years'
    }
  } else if (age <= 12) {
    return {
      label: 'Mature',
      description: 'Fully mature; drink now to enjoy at peak complexity'
    }
  } else {
    return {
      label: 'Fully Mature',
      description: 'Well-aged; best to enjoy soon while still vibrant'
    }
  }
}

function getPriceContext(quality: QualityRating): { label: string; description: string; color: string } {
  switch (quality) {
    case '+':
      return {
        label: 'Premium Pricing',
        description: 'Expect higher prices for wines from excellent vintages',
        color: 'text-green-700'
      }
    case '~':
      return {
        label: 'Value Opportunity',
        description: 'Often good value as prices may be lower despite decent quality',
        color: 'text-yellow-700'
      }
    case '-':
      return {
        label: 'Budget Friendly',
        description: 'Lower prices, but quality varies; choose carefully',
        color: 'text-red-700'
      }
    default:
      return {
        label: 'Unknown',
        description: '',
        color: 'text-gray-700'
      }
  }
}

function WineResultDisplay({ result }: WineResultDisplayProps) {
  const alertClass = getAlertClass(result.quality)
  const qualityLabel = getQualityLabel(result.quality)
  const qualityIcon = getQualityIcon(result.quality)
  const aging = getAgingRecommendation(result.vintage, result.quality)
  const pricing = getPriceContext(result.quality)
  const info = regionInfo[result.region]

  return (
    <div className="space-y-4 mt-6">
      {/* Main Result */}
      <div className={`${alertClass} p-4 rounded-lg`}>
        <div className="flex items-start">
          <span className="text-2xl mr-3">{qualityIcon}</span>
          <div>
            <h3 className="font-semibold text-lg">
              {result.vintage} - {result.region}
            </h3>
            <p className="text-sm font-medium mb-1">{qualityLabel}</p>
            <p className="text-sm">{result.description}</p>
          </div>
        </div>
      </div>

      {/* Additional Info Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Aging Recommendation */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <span className="text-lg mr-2">⏱</span>
            <h4 className="font-medium text-gray-800">Aging Recommendation</h4>
          </div>
          <p className="text-sm font-semibold text-purple-700">{aging.label}</p>
          <p className="text-xs text-gray-600 mt-1">{aging.description}</p>
        </div>

        {/* Price Context */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <span className="text-lg mr-2">$</span>
            <h4 className="font-medium text-gray-800">Price Expectations</h4>
          </div>
          <p className={`text-sm font-semibold ${pricing.color}`}>{pricing.label}</p>
          <p className="text-xs text-gray-600 mt-1">{pricing.description}</p>
        </div>
      </div>

      {/* Wine Pairing & Region Info */}
      {info && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <span className="text-lg mr-2">🍷</span>
            <h4 className="font-medium text-gray-800">{result.region} Wine Guide</h4>
          </div>

          <div className="space-y-3">
            {/* Style */}
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Wine Style</p>
              <p className="text-sm text-gray-700">{info.style}</p>
            </div>

            {/* Key Grapes */}
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Key Grapes</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {info.keyGrapes.map((grape) => (
                  <span key={grape} className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                    {grape}
                  </span>
                ))}
              </div>
            </div>

            {/* Food Pairings */}
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Food Pairings</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {info.pairings.map((pairing) => (
                  <span key={pairing} className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                    {pairing}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default WineResultDisplay
